/**
 * @prettier
 */
import React, { FunctionComponent, useEffect, useState } from "react";
import { Paper, Box, Button } from "@material-ui/core";
import moment from "moment";

import { firestore } from "firebase-tools/firebase";
import { PageType } from "types/PageType";

// Written hourly by the monitoring-freshnessCheck cloud function
const INGESTION_DOC = firestore.collection("system-status").doc("ingestion");
// Written by the Spotify scraper on every run (see the Spotify Status page)
const SPOTIFY_STATUS_DOC = firestore
  .collection("spotify-metrics")
  .doc("scraper-status");

const REFRESH_URL =
  "https://europe-west2-be-measured-admin-production.cloudfunctions.net/monitoring-refreshNow";

const COLORS: Record<string, string> = {
  green: "#2e7d32",
  amber: "#ef6c00",
  red: "#c62828",
  grey: "#616161",
};

const PLATFORM_ORDER = [
  "youtube",
  "twitter",
  "spotify",
  "facebook",
  "instagram",
  "instagramStories",
  "tiktok",
];

const toDate = (value: any): Date | null => {
  if (!value) return null;
  if (typeof value.toDate === "function") return value.toDate();
  if (value._seconds) return new Date(value._seconds * 1000);
  if (typeof value === "string") return new Date(value);
  return null;
};

const platformState = (p: any, spotifyStatus: any) => {
  if (!p) return { level: "grey", note: "no data" };
  if (p.error) return { level: "grey", note: `check error: ${p.error}` };
  if (p.empty) return { level: "grey", note: "collection is empty" };

  const lastWrite = toDate(p.lastWriteAt);
  if (!lastWrite) return { level: "grey", note: "no write timestamp" };

  const hours = moment().diff(moment(lastWrite), "hours");
  const expected = p.expectedIntervalHours || 26;

  if (p.lastDocStatus === "failed") {
    return {
      level: "red",
      note: "job is running but writing FAILURE records",
    };
  }
  if (hours > expected * 3) {
    const age = moment(lastWrite).fromNow(true);
    return { level: "red", note: `no data written for ${age}` };
  }
  if (hours > expected) {
    return { level: "amber", note: `overdue — last write ${hours}h ago` };
  }

  // Spotify only: freshness is fine, but surface session problems early
  if (spotifyStatus?.needsCookieRefresh) {
    return {
      level: "red",
      note: "login session needs refreshing — see Spotify Status page",
    };
  }

  // Twitter only: writes may be fresh while most accounts are silently dead —
  // partial coverage must not show green
  if (typeof p.accountsConnected === "number" && p.accountsConnected > 0) {
    const collecting = p.accountsCollecting || 0;
    if (collecting === 0) {
      return {
        level: "red",
        note: `0 of ${p.accountsConnected} connected accounts collecting`,
      };
    }
    if (collecting < p.accountsConnected) {
      return {
        level: "amber",
        note: `only ${collecting} of ${p.accountsConnected} connected accounts collecting — the rest need re-connecting`,
      };
    }
    return {
      level: "green",
      note: `collecting normally (all ${p.accountsConnected} accounts)`,
    };
  }

  return { level: "green", note: "collecting normally" };
};

export const DataHealthPage: FunctionComponent<PageType> = () => {
  const [ingestion, setIngestion] = useState<any>(undefined);
  const [spotifyStatus, setSpotifyStatus] = useState<any>(null);
  const [loadError, setLoadError] = useState("");
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    const unsub1 = INGESTION_DOC.onSnapshot(
      (snap) => setIngestion(snap.exists ? snap.data() : null),
      (error) => setLoadError(error.message)
    );
    const unsub2 = SPOTIFY_STATUS_DOC.onSnapshot(
      (snap) => setSpotifyStatus(snap.exists ? snap.data() : null),
      () => undefined
    );
    return () => {
      unsub1();
      unsub2();
    };
  }, []);

  const refreshNow = async () => {
    setRefreshing(true);
    try {
      await fetch(REFRESH_URL);
      // onSnapshot delivers the update
    } catch (e) {
      // non-fatal — the hourly schedule still runs
    }
    setRefreshing(false);
  };

  const checkedAt = toDate(ingestion?.checkedAt);
  const platforms = ingestion?.platforms || {};
  const keys = [
    ...PLATFORM_ORDER.filter((k) => platforms[k]),
    ...Object.keys(platforms).filter((k) => !PLATFORM_ORDER.includes(k)),
  ];

  return (
    <Box style={{ maxWidth: 980, margin: "0 auto", padding: "90px 20px 60px" }}>
      <Box
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
        }}
      >
        <h1 style={{ fontWeight: 900 }}>Data Health</h1>
        <Box style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ marginRight: 12, color: "#666" }}>
            Last checked: {checkedAt ? moment(checkedAt).fromNow() : "—"}
          </span>
          <Button
            variant="outlined"
            size="small"
            disabled={refreshing}
            onClick={refreshNow}
          >
            {refreshing ? "Checking…" : "Check now"}
          </Button>
        </Box>
      </Box>

      {loadError && (
        <Paper style={{ padding: 20, marginBottom: 20 }}>
          <p style={{ color: COLORS.red, margin: 0 }}>
            Could not load status: {loadError}
          </p>
        </Paper>
      )}

      {ingestion === null && (
        <Paper style={{ padding: 20, marginBottom: 20 }}>
          <p style={{ margin: 0 }}>
            No health snapshot yet — the monitor runs hourly at half past. Use
            &ldquo;Check now&rdquo; to trigger one.
          </p>
        </Paper>
      )}

      {keys.map((key) => {
        const p = platforms[key];
        const state = platformState(
          p,
          key === "spotify" ? spotifyStatus : null
        );
        const lastWrite = toDate(p.lastWriteAt);
        return (
          <Paper
            key={key}
            style={{
              padding: "14px 20px",
              marginBottom: 10,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderLeft: `8px solid ${COLORS[state.level]}`,
              flexWrap: "wrap",
            }}
          >
            <Box style={{ minWidth: 180 }}>
              <strong style={{ fontSize: 16 }}>{p.label || key}</strong>
              <div style={{ color: "#888", fontSize: 12 }}>{p.collection}</div>
            </Box>
            <Box style={{ flex: 1, minWidth: 220, padding: "0 16px" }}>
              <span style={{ color: COLORS[state.level], fontWeight: 600 }}>
                {state.note}
              </span>
              {p.accountsStale && p.accountsStale.length > 0 && (
                <div style={{ fontSize: 12, color: "#888", marginTop: 4 }}>
                  not collecting: {p.accountsStale.join(", ")}
                </div>
              )}
            </Box>
            <Box style={{ textAlign: "right", minWidth: 210, color: "#555" }}>
              {lastWrite ? (
                <>
                  last write {moment(lastWrite).fromNow()}
                  <div style={{ fontSize: 12, color: "#999" }}>
                    {moment(lastWrite).format("ddd D MMM YYYY, HH:mm")}
                  </div>
                </>
              ) : (
                "—"
              )}
            </Box>
          </Paper>
        );
      })}

      <Paper style={{ padding: 20, marginTop: 24 }}>
        <h3 style={{ marginTop: 0 }}>Reading this page</h3>
        <ul style={{ lineHeight: 1.8, marginBottom: 0 }}>
          <li>
            <strong style={{ color: COLORS.green }}>Green</strong> — data was
            written within the expected interval and the newest record is not a
            failure.
          </li>
          <li>
            <strong style={{ color: COLORS.amber }}>Amber</strong> — a write is
            overdue. Often transient; investigate if it persists for a day.
          </li>
          <li>
            <strong style={{ color: COLORS.red }}>Red</strong> — collection has
            stopped (or is writing failure records). Data is being lost daily
            until fixed.
          </li>
          <li>
            &ldquo;Last write&rdquo; is when a record actually landed in the
            database. The data inside is normally stamped with the previous
            day&rsquo;s date — that is by design, not a fault.
          </li>
        </ul>
      </Paper>
    </Box>
  );
};
